/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/shared/types';

export const elGR: LocalizationResource = {
  locale: 'el-GR',
  apiKeys: {
    action__add: 'Προσθήκη',
    action__search: 'Αναζήτηση',
    copySecret: {
      formButtonPrimary__copyAndClose: 'Αντιγραφή και κλείσιμο',
      formHint: 'Για λόγους ασφαλείας, δεν θα σας επιτρέψουμε να το δείτε ξανά αργότερα.',
      formTitle: 'Αντιγράψτε το κλειδί API "{{name}}" σας τώρα',
    },
    createdAndExpirationStatus__expiresOn: 'Λήγει στις {{expiresDate}}',
    createdAndExpirationStatus__never: 'Δεν λήγει ποτέ',
    detailsTitle__emptyRow: 'Δεν υπάρχουν κλειδιά API προς εμφάνιση',
    formButtonPrimary__add: 'Προσθήκη κλειδιού',
    formFieldCaption__expiration__expiresOn: 'Λήγει στις',
    formFieldCaption__expiration__never: 'Δεν λήγει ποτέ',
    formFieldOption__expiration__180d: '180 ημέρες',
    formFieldOption__expiration__1d: '1 ημέρα',
    formFieldOption__expiration__1y: '1 έτος',
    formFieldOption__expiration__30d: '30 ημέρες',
    formFieldOption__expiration__60d: '60 ημέρες',
    formFieldOption__expiration__7d: '7 ημέρες',
    formFieldOption__expiration__90d: '90 ημέρες',
    formFieldOption__expiration__never: 'Ποτέ',
    formHint: 'Δημιουργήστε ένα κλειδί API για την ενσωμάτωση με εξωτερικές υπηρεσίες.',
    formTitle: 'Δημιουργία κλειδιού API',
    lastUsed__days: 'Τελευταία χρήση πριν {{count}} ημέρες',
    lastUsed__hours: 'Τελευταία χρήση πριν {{count}} ώρες',
    lastUsed__minutes: 'Τελευταία χρήση πριν {{count}} λεπτά',
    lastUsed__months: 'Τελευταία χρήση πριν {{count}} μήνες',
    lastUsed__seconds: 'Τελευταία χρήση πριν {{count}} δευτερόλεπτα',
    lastUsed__years: 'Τελευταία χρήση πριν {{count}} έτη',
    menuAction__revoke: 'Ανάκληση',
    revokeConfirmation: {
      confirmationText: 'Πληκτρολογήστε "{{secretName}}" για να επιβεβαιώσετε',
      formButtonPrimary__revoke: 'Ανάκληση κλειδιού',
      formHint: 'Αυτή η ενέργεια είναι μόνιμη και μη αναστρέψιμη.',
      formTitle: 'Ανάκληση κλειδιού API',
    },
  },
  backButton: 'Πίσω',
  badge__activePlan: 'Ενεργό πλάνο',
  badge__canceledEndsAt: 'Ακυρώθηκε, λήγει {{date | timeString("el-GR")}}',
  badge__currentPlan: 'Τρέχον πλάνο',
  badge__default: 'Προεπιλογή',
  badge__endsAt: 'Λήγει {{date | timeString("el-GR")}}',
  badge__expired: 'Έληξε',
  badge__freeTrial: 'Δωρεάν δοκιμή',
  badge__otherImpersonatorDevice: 'Άλλη συσκευή υποδυόμενου',
  badge__pastDueAt: 'Ληξιπρόθεσμο {{date | timeString("el-GR")}}',
  badge__pastDuePlan: 'Ληξιπρόθεσμο πλάνο',
  badge__primary: 'Κύριο',
  badge__renewsAt: 'Ανανεώνεται {{date | timeString("el-GR")}}',
  badge__requiresAction: 'Απαιτεί ενέργεια',
  badge__startsAt: 'Ξεκινά {{date | timeString("el-GR")}}',
  badge__thisDevice: 'Αυτή η συσκευή',
  badge__trialEndsAt: 'Η δοκιμή λήγει {{date | timeString("el-GR")}}',
  badge__unverified: 'Μη επαληθευμένο',
  badge__upcomingPlan: 'Επερχόμενο πλάνο',
  badge__userDevice: 'Συσκευή χρήστη',
  badge__you: 'Εσείς',
  billing: {
    addPaymentMethod__label: 'Προσθήκη μεθόδου πληρωμής',
    alwaysFree: 'Πάντα δωρεάν',
    annually: 'Ετήσια',
    availableFeatures: 'Διαθέσιμα χαρακτηριστικά',
    billedAnnually: 'Χρέωση ετησίως',
    billedMonthlyOnly: 'Χρέωση μόνο μηνιαίως',
    cancelFreeTrial: 'Ακύρωση δωρεάν δοκιμής',
    cancelFreeTrialAccessUntil: 'Θα έχετε πρόσβαση μέχρι τις {{date | timeString("el-GR")}}',
    cancelFreeTrialTitle: 'Ακύρωση δωρεάν δοκιμής;',
    cancelSubscription: 'Ακύρωση συνδρομής',
    cancelSubscriptionAccessUntil: 'Θα έχετε πρόσβαση μέχρι τις {{date | timeString("el-GR")}}',
    cancelSubscriptionNoCharge: 'Δεν θα χρεωθείτε ξανά',
    cancelSubscriptionPastDue: 'Η συνδρομή σας είναι ληξιπρόθεσμη. Η ακύρωση θα διαγράψει όλα τα μη καταβληθέντα ποσά.',
    cancelSubscriptionTitle: 'Ακύρωση συνδρομής;',
    cannotSubscribeMonthly: 'Δεν μπορείτε να εγγραφείτε μηνιαίως σε αυτό το πλάνο',
    cannotSubscribeUnrecoverable: 'Δεν μπορείτε να εγγραφείτε σε αυτό το πλάνο αυτήν τη στιγμή',
    checkout: {
      description__paymentSuccessful: 'Η πληρωμή σας ολοκληρώθηκε επιτυχώς',
      description__subscriptionSuccessful: 'Η συνδρομή σας ξεκίνησε επιτυχώς',
      downgradeNotice: 'Θα υποβαθμιστείτε στο τέλος της τρέχουσας περιόδου χρέωσης',
      emailForm: {
        subtitle: 'Εισάγετε τη διεύθυνση email σας για να συνεχίσετε',
        title: 'Στοιχεία πληρωμής',
      },
      lineItems: {
        title__freeTrialEndsAt: 'Η δοκιμή λήγει',
        title__paymentMethod: 'Μέθοδος πληρωμής',
        title__statementId: 'ID κατάστασης',
        title__subscriptionBegins: 'Η συνδρομή ξεκινά',
        title__totalPaid: 'Σύνολο που καταβλήθηκε',
      },
      pastDueNotice: 'Η συνδρομή σας είναι ληξιπρόθεσμη. Παρακαλώ ενημερώστε τη μέθοδο πληρωμής σας.',
      perMonth: 'ανά μήνα',
      title: 'Ολοκλήρωση πληρωμής',
      title__paymentSuccessful: 'Επιτυχής πληρωμή',
      title__subscriptionSuccessful: 'Επιτυχής συνδρομή',
      title__trialSuccess: 'Επιτυχής δωρεάν δοκιμή',
      totalDueAfterTrial: 'Σύνολο οφειλής μετά τη δοκιμή',
    },
    credit: 'Πίστωση',
    creditRemainder: 'Υπόλοιπο πίστωσης',
    defaultFreePlanActive: 'Το προεπιλεγμένο δωρεάν πλάνο είναι ενεργό',
    free: 'Δωρεάν',
    getStarted: 'Ξεκινήστε',
    keepFreeTrial: 'Διατήρηση δωρεάν δοκιμής',
    keepSubscription: 'Διατήρηση συνδρομής',
    manage: 'Διαχείριση',
    manageSubscription: 'Διαχείριση συνδρομής',
    month: 'μήνας',
    monthly: 'Μηνιαία',
    pastDue: 'Ληξιπρόθεσμο',
    pay: 'Πληρωμή',
    paymentMethod: {
      applePayDescription: {
        annual: '{{price}} ετησίως',
        monthly: '{{price}} μηνιαίως',
      },
      dev: {
        anyNumbers: 'Οποιοιδήποτε αριθμοί',
        cardNumber: 'Αριθμός κάρτας',
        cvcZip: 'CVC/Ταχ. Κωδ.',
        developmentMode: 'Λειτουργία ανάπτυξης',
        expirationDate: 'Ημερομηνία λήξης',
        testCardInfo: 'Χρησιμοποιήστε τα παρακάτω στοιχεία δοκιμαστικής κάρτας',
      },
    },
    paymentMethods__label: 'Μέθοδοι πληρωμής',
    highlightedPlanBadge: 'Δημοφιλές',
    pricingTable: {
      billingCycle: 'Κύκλος χρέωσης',
      included: 'Περιλαμβάνεται',
    },
    reSubscribe: 'Επανεγγραφή',
    seeAllFeatures: 'Δείτε όλα τα χαρακτηριστικά',
    startFreeTrial: 'Έναρξη δωρεάν δοκιμής',
    startFreeTrial__days: 'Έναρξη δωρεάν δοκιμής {{days}} ημερών',
    subscribe: 'Εγγραφή',
    subscriptionDetails: {
      beginsOn: 'Ξεκινά στις',
      currentBillingCycle: 'Τρέχων κύκλος χρέωσης',
      endsOn: 'Λήγει στις',
      firstPaymentAmount: 'Ποσό πρώτης πληρωμής',
      firstPaymentOn: 'Πρώτη πληρωμή στις',
      nextPaymentAmount: 'Ποσό επόμενης πληρωμής',
      nextPaymentOn: 'Επόμενη πληρωμή στις',
      pastDueAt: 'Ληξιπρόθεσμο στις',
      renewsAt: 'Ανανεώνεται στις',
      subscribedOn: 'Εγγραφή στις',
      title: 'Λεπτομέρειες συνδρομής',
      trialEndsOn: 'Η δοκιμή λήγει στις',
      trialStartedOn: 'Η δοκιμή ξεκίνησε στις',
    },
    subtotal: 'Υποσύνολο',
    switchPlan: 'Αλλαγή πλάνου',
    switchToAnnual: 'Αλλαγή σε ετήσια χρέωση',
    switchToAnnualWithAnnualPrice: 'Αλλαγή σε ετήσια χρέωση για {{price}}/έτος',
    switchToMonthly: 'Αλλαγή σε μηνιαία χρέωση',
    switchToMonthlyWithPrice: 'Αλλαγή σε μηνιαία χρέωση για {{price}}/μήνα',
    totalDue: 'Συνολική οφειλή',
    totalDueToday: 'Συνολική οφειλή σήμερα',
    viewFeatures: 'Προβολή χαρακτηριστικών',
    viewPayment: 'Προβολή πληρωμής',
    year: 'έτος',
  },
  configureSSO: {
    missingManageEnterpriseConnectionsPermission: {
      subtitle:
        'Επικοινωνήστε με τον διαχειριστή του οργανισμού σας για να αποκτήσετε άδειες διαχείρισης εταιρικών συνδέσεων.',
      title: 'Δεν έχετε άδεια διαχείρισης εταιρικών συνδέσεων',
    },
    navbar: {
      title: 'Διαμόρφωση Ενιαίας Σύνδεσης (SSO)',
    },
    selectProviderStep: {
      title: 'Επιλέξτε πάροχο',
      subtitle: 'Επιλέξτε τον πάροχο για τον οποίο θα ρυθμίσετε SSO.',
      body: {
        title: 'Επιλέξτε τον πάροχο ταυτότητάς σας',
        description: 'Θα σας καθοδηγήσουμε στη λεπτομερή διαδικασία ρύθμισης στη συνέχεια.',
      },
      saml: {
        groupLabel: 'SAML',
        okta: 'Okta Workforce',
        customSaml: 'Προσαρμοσμένος πάροχος SAML',
      },
      warning: 'Μόλις επιλεγεί ένας πάροχος δεν μπορείτε να τον αλλάξετε μέχρι να ολοκληρωθεί η ρύθμιση',
    },
    verifyEmailDomainStep: {
      title: 'Επαλήθευση διεύθυνσης email',
      subtitle: 'Επαληθεύστε τη διεύθυνση email στην οποία θέλετε να ενεργοποιήσετε τη σύνδεση επιχείρησης.',
      addEmailAddress: {
        formTitle: 'Χρειαζόμαστε το email σας',
        formSubtitle: 'Για να ξεκινήσουμε, θα χρειαστούμε τη διεύθυνση email σας',
        inputPlaceholder: 'name@company.com',
        inputLabel: 'Διεύθυνση email',
      },
      emailCode: {
        formTitle: 'Επαληθεύστε τη διεύθυνση email σας',
        formSubtitle: 'Εισαγάγετε τον κωδικό επαλήθευσης που στάλθηκε στο {{identifier}}',
        resendButton: 'Δεν λάβατε κωδικό; Επαναποστολή',
        verified: {
          title: 'Λάβαμε το email σας',
          subtitle: 'Έχετε επαληθεύσει τη διεύθυνση email σας με το ακόλουθο email',
          inputLabel: 'Επαληθευμένη διεύθυνση email',
        },
      },
      domainTaken: {
        title: 'Αυτός ο τομέας ({{domain}}) διαθέτει ήδη σύνδεση SSO',
        subtitle:
          'Επικοινωνήστε με τον διαχειριστή της εφαρμογής για να αποκτήσετε πρόσβαση μέσω της υπάρχουσας σύνδεσης.',
      },
    },
  },
  createOrganization: {
    formButtonSubmit: 'Δημιουργία οργανισμού',
    invitePage: {
      formButtonReset: 'Παράλειψη',
    },
    title: 'Δημιουργία Οργανισμού',
  },
  dates: {
    lastDay: "Χθες στις {{ date | timeString('el') }}",
    next6Days: "{{ date | weekday('el','long') }} στις {{ date | timeString('el') }}",
    nextDay: "Αύριο στις {{ date | timeString('el') }}",
    numeric: "{{ date | numeric('el') }}",
    previous6Days: "Τελευταία {{ date | weekday('el','long') }} στις {{ date | timeString('el') }}",
    sameDay: "Σήμερα στις {{ date | timeString('el') }}",
  },
  dividerText: 'ή',
  footerActionLink__alternativePhoneCodeProvider: 'Λάβετε κωδικό με άλλο τρόπο',
  footerActionLink__useAnotherMethod: 'Χρησιμοποιήστε άλλη μέθοδο',
  footerPageLink__help: 'Βοήθεια',
  footerPageLink__privacy: 'Προστασία προσωπικών δεδομένων',
  footerPageLink__terms: 'Όροι',
  formButtonPrimary: 'Συνέχεια',
  formButtonPrimary__verify: 'Επαλήθευση',
  formFieldAction__forgotPassword: 'Ξεχάσατε τον κωδικό;',
  formFieldError__matchingPasswords: 'Οι κωδικοί ταιριάζουν.',
  formFieldError__notMatchingPasswords: 'Οι κωδικοί δεν ταιριάζουν.',
  formFieldError__verificationLinkExpired: 'Ο σύνδεσμος επαλήθευσης έληξε. Παρακαλώ ζητήστε νέο σύνδεσμο.',
  formFieldHintText__optional: 'Προαιρετικό',
  formFieldHintText__slug:
    'Ένα slug είναι ένα αναγνωριστικό αναγνώσιμο από ανθρώπους που πρέπει να είναι μοναδικό. Χρησιμοποιείται συχνά σε URLs.',
  formFieldInputPlaceholder__apiKeyDescription: 'Προσθέστε μια περιγραφή (προαιρετικό)',
  formFieldInputPlaceholder__apiKeyExpirationDate: 'Επιλέξτε ημερομηνία λήξης',
  formFieldInputPlaceholder__apiKeyName: 'Εισάγετε όνομα κλειδιού',
  formFieldInputPlaceholder__backupCode: 'Εφεδρικός κωδικός',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Delete account',
  formFieldInputPlaceholder__emailAddress: 'Εισάγετε τη διεύθυνση email σας',
  formFieldInputPlaceholder__emailAddress_username: 'Εισάγετε email ή όνομα χρήστη',
  formFieldInputPlaceholder__emailAddresses:
    'Εισαγάγετε ή επικολλήστε μία ή περισσότερες διευθύνσεις email, χωρισμένες με κενά ή κόμματα',
  formFieldInputPlaceholder__firstName: 'Όνομα',
  formFieldInputPlaceholder__lastName: 'Επώνυμο',
  formFieldInputPlaceholder__organizationDomain: 'example.com',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'you@example.com',
  formFieldInputPlaceholder__organizationName: 'Όνομα οργανισμού',
  formFieldInputPlaceholder__organizationSlug: 'my-org',
  formFieldInputPlaceholder__password: 'Εισάγετε τον κωδικό σας',
  formFieldInputPlaceholder__phoneNumber: 'Εισάγετε τον αριθμό τηλεφώνου σας',
  formFieldInputPlaceholder__username: 'Εισάγετε όνομα χρήστη',
  formFieldInput__emailAddress_format: 'Διεύθυνση email',
  formFieldLabel__apiKey: 'Κλειδί API',
  formFieldLabel__apiKeyDescription: 'Περιγραφή',
  formFieldLabel__apiKeyExpiration: 'Λήξη',
  formFieldLabel__apiKeyName: 'Όνομα μυστικού κλειδιού',
  formFieldLabel__automaticInvitations: 'Ενεργοποίηση αυτόματων προσκλήσεων για αυτόν τον τομέα',
  formFieldLabel__backupCode: 'Αντίγραφο ασφαλείας κωδικού',
  formFieldLabel__confirmDeletion: 'Επιβεβαίωση',
  formFieldLabel__confirmPassword: 'Επιβεβαίωση κωδικού πρόσβασης',
  formFieldLabel__currentPassword: 'Τρέχων κωδικός πρόσβασης',
  formFieldLabel__emailAddress: 'Διεύθυνση email',
  formFieldLabel__emailAddress_username: 'Διεύθυνση email ή όνομα χρήστη',
  formFieldLabel__emailAddresses: 'Διευθύνσεις email',
  formFieldLabel__firstName: 'Όνομα',
  formFieldLabel__lastName: 'Επώνυμο',
  formFieldLabel__newPassword: 'Νέος κωδικός πρόσβασης',
  formFieldLabel__organizationDomain: 'Domain',
  formFieldLabel__organizationDomainDeletePending: 'Διαγραφή εκκρεμών προσκλήσεων και προτάσεων',
  formFieldLabel__organizationDomainEmailAddress: 'Διεύθυνση email επαλήθευσης',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Εισάγετε μια διεύθυνση email σε αυτόν τον τομέα για να λάβετε κωδικό και να επαληθεύσετε τον τομέα.',
  formFieldLabel__organizationName: 'Όνομα οργανισμού',
  formFieldLabel__organizationSlug: 'Συντόμευση URL',
  formFieldLabel__passkeyName: 'Όνομα Passkey',
  formFieldLabel__password: 'Κωδικός πρόσβασης',
  formFieldLabel__phoneNumber: 'Αριθμός τηλεφώνου',
  formFieldLabel__role: 'Ρόλος',
  formFieldLabel__signOutOfOtherSessions: 'Αποσύνδεση από όλες τις άλλες συσκευές',
  formFieldLabel__username: 'Όνομα χρήστη',
  impersonationFab: {
    action__signOut: 'Αποσύνδεση',
    title: 'Είστε συνδεδεμένος ως {{identifier}}',
  },
  lastAuthenticationStrategy: 'Τελευταία χρήση',
  maintenanceMode:
    'Βρισκόμαστε αυτήν τη στιγμή σε λειτουργία συντήρησης, αλλά μην ανησυχείτε, δεν θα διαρκέσει πάνω από λίγα λεπτά.',
  membershipRole__admin: 'Διαχειριστής',
  membershipRole__basicMember: 'Μέλος',
  membershipRole__guestMember: 'Επισκέπτης',
  organizationList: {
    action__createOrganization: 'Δημιουργία οργανισμού',
    action__invitationAccept: 'Συμμετοχή',
    action__suggestionsAccept: 'Αίτηση συμμετοχής',
    createOrganization: 'Δημιουργία Οργανισμού',
    invitationAcceptedLabel: 'Συμμετέχετε',
    subtitle: 'για να συνεχίσετε στο {{applicationName}}',
    suggestionsAcceptedLabel: 'Εκκρεμεί έγκριση',
    title: 'Επιλέξτε λογαριασμό',
    titleWithoutPersonal: 'Επιλέξτε οργανισμό',
  },
  organizationProfile: {
    apiKeysPage: {
      title: 'Κλειδιά API',
    },
    badge__automaticInvitation: 'Αυτόματες προσκλήσεις',
    badge__automaticSuggestion: 'Αυτόματες προτάσεις',
    badge__manualInvitation: 'Χωρίς αυτόματη εγγραφή',
    badge__unverified: 'Μη επαληθευμένο',
    billingPage: {
      paymentHistorySection: {
        empty: 'Δεν υπάρχει ιστορικό πληρωμών',
        notFound: 'Δεν βρέθηκαν πληρωμές',
        tableHeader__amount: 'Ποσό',
        tableHeader__date: 'Ημερομηνία',
        tableHeader__status: 'Κατάσταση',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Ορισμός ως προεπιλογή',
        actionLabel__remove: 'Αφαίρεση',
        add: 'Προσθήκη μεθόδου πληρωμής',
        addSubtitle: 'Προσθέστε μια νέα μέθοδο πληρωμής στον λογαριασμό σας',
        cancelButton: 'Ακύρωση',
        formButtonPrimary__add: 'Προσθήκη',
        formButtonPrimary__pay: 'Πληρωμή',
        payWithTestCardButton: 'Πληρωμή με δοκιμαστική κάρτα',
        removeMethod: {
          messageLine1: 'Η μέθοδος πληρωμής {{identifier}} θα αφαιρεθεί από τον λογαριασμό σας',
          messageLine2: 'Δεν θα μπορείτε πλέον να χρησιμοποιήσετε αυτή τη μέθοδο πληρωμής',
          successMessage: 'Η μέθοδος πληρωμής αφαιρέθηκε επιτυχώς',
          title: 'Αφαίρεση μεθόδου πληρωμής',
        },
        title: 'Μέθοδοι πληρωμής',
      },
      start: {
        headerTitle__payments: 'Πληρωμές',
        headerTitle__plans: 'Πλάνα',
        headerTitle__statements: 'Καταστάσεις',
        headerTitle__subscriptions: 'Συνδρομές',
      },
      statementsSection: {
        empty: 'Δεν υπάρχουν καταστάσεις',
        itemCaption__paidForPlan: 'Πληρώθηκε για {{planName}}',
        itemCaption__proratedCredit: 'Πίστωση αναλογικής κατανομής',
        itemCaption__subscribedAndPaidForPlan: 'Εγγραφή και πληρωμή για {{planName}}',
        notFound: 'Δεν βρέθηκαν καταστάσεις',
        tableHeader__amount: 'Ποσό',
        tableHeader__date: 'Ημερομηνία',
        title: 'Καταστάσεις',
        totalPaid: 'Συνολικό καταβληθέν ποσό',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Διαχείριση συνδρομής',
        actionLabel__newSubscription: 'Νέα συνδρομή',
        actionLabel__switchPlan: 'Αλλαγή πλάνου',
        tableHeader__edit: 'Επεξεργασία',
        tableHeader__plan: 'Πλάνο',
        tableHeader__startDate: 'Ημερομηνία έναρξης',
        title: 'Συνδρομές',
      },
      subscriptionsSection: {
        actionLabel__default: 'Προεπιλογή',
      },
      switchPlansSection: {
        title: 'Αλλαγή πλάνου',
      },
      title: 'Χρεώσεις',
    },
    createDomainPage: {
      subtitle:
        'Προσθέστε τον τομέα για επαλήθευση. Χρήστες με διευθύνσεις email σε αυτόν τον τομέα μπορούν να συμμετάσχουν στον οργανισμό αυτόματα ή να αιτηθούν συμμετοχή.',
      title: 'Προσθήκη τομέα',
    },
    invitePage: {
      detailsTitle__inviteFailed:
        'Οι προσκλήσεις δεν μπορούσαν να σταλούν. Διορθώστε τα παρακάτω στοιχεία και δοκιμάστε ξανά:',
      formButtonPrimary__continue: 'Αποστολή προσκλήσεων',
      selectDropdown__role: 'Επιλογή ρόλου',
      subtitle: 'Προσκαλέστε νέα μέλη σε αυτόν τον οργανισμό',
      successMessage: 'Οι προσκλήσεις εστάλησαν με επιτυχία',
      title: 'Πρόσκληση μελών',
    },
    membersPage: {
      action__invite: 'Πρόσκληση',
      action__search: 'Αναζήτηση',
      activeMembersTab: {
        menuAction__remove: 'Αφαίρεση μέλους',
        tableHeader__actions: 'Ενέργειες',
        tableHeader__joined: 'Εγγραφήκατε',
        tableHeader__role: 'Ρόλος',
        tableHeader__user: 'Χρήστης',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle:
            'Ενημερώνουμε τους διαθέσιμους ρόλους. Μόλις ολοκληρωθεί, θα μπορείτε να ενημερώσετε ξανά τους ρόλους.',
          title: 'Οι ρόλοι είναι προσωρινά κλειδωμένοι',
        },
      },
      detailsTitle__emptyRow: 'Δεν υπάρχουν μέλη για εμφάνιση',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Προσκαλέστε χρήστες συνδέοντας έναν τομέα email με τον οργανισμό σας. Οποιοσδήποτε εγγραφεί με έναν αντίστοιχο τομέα email θα μπορεί να συμμετάσχει στον οργανισμό ανά πάσα στιγμή.',
          headerTitle: 'Αυτόματες προσκλήσεις',
          primaryButton: 'Διαχείριση επαληθευμένων τομέων',
        },
        table__emptyRow: 'Δεν υπάρχουν προσκλήσεις προς εμφάνιση',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Ανάκληση πρόσκλησης',
        tableHeader__invited: 'Προσκεκλημένο',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Οι χρήστες που εγγράφονται με αντίστοιχο τομέα email, θα μπορούν να δουν μια πρόταση να ζητήσουν συμμετοχή στον οργανισμό σας.',
          headerTitle: 'Αυτόματες προτάσεις',
          primaryButton: 'Διαχείριση επαληθευμένων τομέων',
        },
        menuAction__approve: 'Έγκριση',
        menuAction__reject: 'Απόρριψη',
        tableHeader__requested: 'Ζητήθηκε πρόσβαση',
        table__emptyRow: 'Δεν υπάρχουν αιτήματα προς εμφάνιση',
      },
      start: {
        headerTitle__invitations: 'Προσκλήσεις',
        headerTitle__members: 'Μέλη',
        headerTitle__requests: 'Αιτήματα',
      },
    },
    navbar: {
      apiKeys: 'Κλειδιά API',
      billing: 'Χρεώσεις',
      description: 'Διαχείριση του οργανισμού σας.',
      general: 'Γενικά',
      members: 'Μέλη',
      title: 'Οργανισμός',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: 'Δεν έχετε δικαιώματα για διαχείριση χρεώσεων',
      },
      title: 'Πλάνα',
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Πληκτρολογήστε το {{organizationName}} παρακάτω για να συνεχίσετε.',
          messageLine1: 'Είστε σίγουρος ότι θέλετε να διαγράψετε αυτόν τον οργανισμό;',
          messageLine2: 'Αυτή η ενέργεια είναι μόνιμη και και μη αναστρέψιμη.',
          successMessage: 'Έχετε διαγράψει τον οργανισμό.',
          title: 'Διαγραφή οργανισμού',
        },
        leaveOrganization: {
          actionDescription: 'Πληκτρολογήστε το {{organizationName}} παρακάτω για να συνεχίσετε.',
          messageLine1:
            'Είστε σίγουρος ότι θέλετε να αποχωρήσετε από αυτόν τον οργανισμό; Θα χάσετε την πρόσβαση σε αυτόν τον οργανισμό και τις εφαρμογές του.',
          messageLine2: 'Αυτή η ενέργεια είναι μόνιμη και και μη αναστρέψιμη.',
          successMessage: 'Έχετε αποχωρήσει από τον οργανισμό.',
          title: 'Αποχώρηση από τον οργανισμό',
        },
        title: 'Κίνδυνος',
      },
      domainSection: {
        menuAction__manage: 'Διαχείριση',
        menuAction__remove: 'Διαγραφή',
        menuAction__verify: 'Επαλήθευση',
        primaryButton: 'Προσθήκη τομέα',
        subtitle:
          'Επιτρέψτε στους χρήστες να συμμετάσχουν στον οργανισμό αυτόματα ή να ζητήσουν συμμετοχή με βάση έναν επαληθευμένο τομέα email.',
        title: 'Επαληθευμένοι τομείς',
      },
      successMessage: 'Ο οργανισμός έχει ενημερωθεί.',
      title: 'Προφίλ Οργανισμού',
    },
    removeDomainPage: {
      messageLine1: 'Ο τομέας email {{domain}} θα αφαιρεθεί.',
      messageLine2: 'Οι χρήστες δεν θα μπορούν να συμμετάσχουν στον οργανισμό αυτόματα μετά από αυτό.',
      successMessage: 'Ο τομέας {{domain}} αφαιρέθηκε.',
      title: 'Αφαίρεση τομέα',
    },
    start: {
      headerTitle__general: 'Γενικά',
      headerTitle__members: 'Μέλη',
      profileSection: {
        primaryButton: 'Ενημέρωση προφίλ',
        title: 'Προφίλ οργανισμού',
        uploadAction__title: 'Λογότυπο',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'Η αφαίρεση αυτού του τομέα θα επηρεάσει τους προσκεκλημένους χρήστες.',
        removeDomainActionLabel__remove: 'Αφαίρεση τομέα',
        removeDomainSubtitle: 'Αφαίρεση αυτού του τομέα από τους επαληθευμένους τομείς σας',
        removeDomainTitle: 'Αφαίρεση τομέα',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'Οι χρήστες προσκαλούνται αυτόματα να συμμετάσχουν στον οργανισμό όταν εγγραφούν και μπορούν να συμμετάσχουν ανά πάσα στιγμή.',
        automaticInvitationOption__label: 'Αυτόματες προσκλήσεις',
        automaticSuggestionOption__description:
          'Οι χρήστες λαμβάνουν μια πρόταση να ζητήσουν συμμετοχή, αλλά πρέπει να εγκριθούν από έναν διαχειριστή πριν μπορέσουν να συμμετάσχουν στον οργανισμό.',
        automaticSuggestionOption__label: 'Αυτόματες προτάσεις',
        calloutInfoLabel: 'Η αλλαγή του τρόπου εγγραφής θα επηρεάσει μόνο νέους χρήστες.',
        calloutInvitationCountLabel: 'Εκκρεμείς προσκλήσεις που στάλθηκαν σε χρήστες: {{count}}',
        calloutSuggestionCountLabel: 'Εκκρεμείς προτάσεις που στάλθηκαν σε χρήστες: {{count}}',
        manualInvitationOption__description: 'Οι χρήστες μπορούν να προσκληθούν μόνο χειροκίνητα στον οργανισμό.',
        manualInvitationOption__label: 'Χωρίς αυτόματη εγγραφή',
        subtitle: 'Επιλέξτε πώς οι χρήστες από αυτόν τον τομέα μπορούν να συμμετάσχουν στον οργανισμό.',
      },
      start: {
        headerTitle__danger: 'Επικίνδυνη ζώνη',
        headerTitle__enrollment: 'Επιλογές εγγραφής',
      },
      subtitle: 'Ο τομέας {{domain}} είναι πλέον επαληθευμένος. Συνεχίστε επιλέγοντας τον τρόπο εγγραφής.',
      title: 'Ενημέρωση {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Εισάγετε τον κωδικό επαλήθευσης που στάλθηκε στη διεύθυνση email σας',
      formTitle: 'Κωδικός επαλήθευσης',
      resendButton: 'Δεν λάβατε κωδικό; Επαναποστολή',
      subtitle: 'Ο τομέας {{domainName}} πρέπει να επαληθευτεί μέσω email.',
      subtitleVerificationCodeScreen:
        'Ένας κωδικός επαλήθευσης στάλθηκε στο {{emailAddress}}. Εισάγετε τον κωδικό για να συνεχίσετε.',
      title: 'Επαλήθευση τομέα',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: 'Κλείσιμο επιλογέα οργανισμού',
    action__createOrganization: 'Δημιουργία οργανισμού',
    action__invitationAccept: 'Συμμετοχή',
    action__manageOrganization: 'Διαχείριση οργανισμού',
    action__openOrganizationSwitcher: 'Άνοιγμα επιλογέα οργανισμού',
    action__suggestionsAccept: 'Αίτηση συμμετοχής',
    notSelected: 'Δεν επιλέχθηκε οργανισμός',
    personalWorkspace: 'Προσωπικός χώρος εργασίας',
    suggestionsAcceptedLabel: 'Εκκρεμεί έγκριση',
  },
  paginationButton__next: 'Επόμενο',
  paginationButton__previous: 'Προηγούμενο',
  paginationRowText__displaying: 'Εμφάνιση',
  paginationRowText__of: 'από',
  reverification: {
    alternativeMethods: {
      actionLink: 'Λάβετε βοήθεια',
      actionText: 'Δεν έχετε κάποιο από αυτά;',
      blockButton__backupCode: 'Χρησιμοποιήστε εφεδρικό κωδικό',
      blockButton__emailCode: 'Αποστολή κωδικού στο {{identifier}}',
      blockButton__passkey: 'Χρησιμοποιήστε το passkey σας',
      blockButton__password: 'Σύνδεση με τον κωδικό πρόσβασής σας',
      blockButton__phoneCode: 'Αποστολή κωδικού μέσω SMS στο {{identifier}}',
      blockButton__totp: 'Χρησιμοποιήστε την εφαρμογή επαλήθευσης',
      getHelp: {
        blockButton__emailSupport: 'Επικοινωνία με υποστήριξη email',
        content:
          'Εάν αντιμετωπίζετε δυσκολίες στην επιβεβαίωση της ταυτότητάς σας, στείλτε μας email και θα συνεργαστούμε μαζί σας για να αποκαταστήσουμε την πρόσβαση το συντομότερο δυνατόν.',
        title: 'Λάβετε βοήθεια',
      },
      subtitle:
        'Αντιμετωπίζετε προβλήματα; Μπορείτε να χρησιμοποιήσετε οποιαδήποτε από αυτές τις μεθόδους για επιβεβαίωση.',
      title: 'Χρησιμοποιήστε άλλη μέθοδο',
    },
    backupCodeMfa: {
      subtitle: 'για να συνεχίσετε',
      title: 'Εισάγετε εφεδρικό κωδικό',
    },
    emailCode: {
      formTitle: 'Κωδικός επαλήθευσης',
      resendButton: 'Δεν λάβατε κωδικό; Επαναποστολή',
      subtitle: 'για να συνεχίσετε',
      title: 'Ελέγξτε το email σας',
    },
    noAvailableMethods: {
      message: 'Δεν είναι δυνατή η συνέχεια. Δεν υπάρχει διαθέσιμος παράγοντας επαλήθευσης.',
      subtitle: 'Παρουσιάστηκε σφάλμα',
      title: 'Δεν είναι δυνατή η συνέχεια',
    },
    passkey: {
      blockButton__passkey: 'Χρησιμοποιήστε το passkey σας',
      subtitle:
        'Η χρήση του passkey σας επαληθεύει ότι είστε εσείς. Η συσκευή σας μπορεί να ζητήσει το δακτυλικό σας αποτύπωμα, την αναγνώριση προσώπου ή το PIN οθόνης.',
      title: 'Χρησιμοποιήστε το passkey σας',
    },
    password: {
      actionLink: 'Χρησιμοποιήστε άλλη μέθοδο',
      subtitle: 'για να συνεχίσετε',
      title: 'Εισάγετε τον κωδικό πρόσβασής σας',
    },
    phoneCode: {
      formTitle: 'Κωδικός επαλήθευσης',
      resendButton: 'Δεν λάβατε κωδικό; Επαναποστολή',
      subtitle: 'για να συνεχίσετε',
      title: 'Ελέγξτε το τηλέφωνό σας',
    },
    phoneCodeMfa: {
      formTitle: 'Κωδικός επαλήθευσης',
      resendButton: 'Δεν λάβατε κωδικό; Επαναποστολή',
      subtitle: 'για να συνεχίσετε',
      title: 'Ελέγξτε το τηλέφωνό σας',
    },
    totpMfa: {
      formTitle: 'Κωδικός επαλήθευσης',
      subtitle: 'για να συνεχίσετε',
      title: 'Επαλήθευση δύο παραγόντων',
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Προσθήκη λογαριασμού',
      action__signOutAll: 'Αποσύνδεση από όλους τους λογαριασμούς',
      subtitle: 'Επιλέξτε τον λογαριασμό με τον οποίο θέλετε να συνεχίσετε.',
      title: 'Επιλέξτε λογαριασμό',
    },
    alternativeMethods: {
      actionLink: 'Λήψη βοήθειας',
      actionText: 'Δεν έχετε κανένα από αυτά;',
      blockButton__backupCode: 'Χρήση ενός εφεδρικού κωδικού',
      blockButton__emailCode: 'Αποστολή κωδικού με email στο {{identifier}}',
      blockButton__emailLink: 'Αποστολή συνδέσμου στο {{identifier}}',
      blockButton__passkey: 'Σύνδεση με passkey',
      blockButton__password: 'Σύνδεση με τον κωδικό πρόσβασής σας',
      blockButton__phoneCode: 'Αποστολή κωδικού SMS στο {{identifier}}',
      blockButton__totp: 'Χρήση της εφαρμογής αυθεντικοποίησης',
      getHelp: {
        blockButton__emailSupport: 'Υποστήριξη μέσω email',
        content:
          'Εάν αντιμετωπίζετε δυσκολία στη σύνδεση στον λογαριασμό σας, στείλτε μας email και θα επικοινωνήσουμε μαζί σας για να αποκαταστήσουμε την πρόσβαση το συντομότερο δυνατόν.',
        title: 'Λήψη βοήθειας',
      },
      subtitle:
        'Αντιμετωπίζετε δυσκολίες; Μπορείτε να χρησιμοποιήσετε οποιαδήποτε από αυτές τις μεθόδους για να συνδεθείτε.',
      title: 'Χρήση μιας άλλης μεθόδου',
    },
    alternativePhoneCodeProvider: {
      formTitle: 'Κωδικός επαλήθευσης',
      resendButton: 'Δεν λάβατε κωδικό; Επαναποστολή',
      subtitle: 'Λάβετε έναν κωδικό μέσω {{provider}}',
      title: 'Λάβετε κωδικό με άλλο τρόπο',
    },
    backupCodeMfa: {
      subtitle: 'για να συνεχίσετε στο {{applicationName}}',
      title: 'Εισαγωγή ενός εφεδρικού κωδικού',
    },
    emailCode: {
      formTitle: 'Κωδικός επαλήθευσης',
      resendButton: 'Δεν λάβατε κωδικό; Αποστολή ξανά',
      subtitle: 'για να συνεχίσετε στο {{applicationName}}',
      title: 'Ελέγξτε το email σας',
    },
    emailCodeMfa: {
      formTitle: 'Ελέγξτε το email σας',
      resendButton: 'Δεν λάβατε κωδικό; Αποστολή ξανά',
      subtitle: 'για να συνεχίσετε στο {{applicationName}}',
      title: 'Ελέγξτε το email σας',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'Για να συνεχίσετε, ανοίξτε το σύνδεσμο επαλήθευσης στην ίδια συσκευή και φυλλομετρητή που τον ζητήσατε.',
        title: 'Δεν βρέθηκε έγκυρος σύνδεσμος επαλήθευσης',
      },
      expired: {
        subtitle: 'Επιστροφή στην αρχική καρτέλα για να συνεχίσετε.',
        title: 'Αυτός ο σύνδεσμος επαλήθευσης έχει λήξει',
      },
      failed: {
        subtitle: 'Επιστροφή στην αρχική καρτέλα για να συνεχίσετε.',
        title: 'Αυτός ο σύνδεσμος επαλήθευσης δεν είναι έγκυρος',
      },
      formSubtitle: 'Χρησιμοποιήστε τον σύνδεσμο επαλήθευσης που απεστάλη στο email σας',
      formTitle: 'Σύνδεσμος επαλήθευσης',
      loading: {
        subtitle: 'Θα ανακατευθυνθείτε σύντομα',
        title: 'Σύνδεση σε εξέλιξη...',
      },
      resendButton: 'Δεν λάβατε σύνδεσμο; Αποστολή ξανά',
      subtitle: 'για να συνεχίσετε στο {{applicationName}}',
      title: 'Ελέγξτε το email σας',
      unusedTab: {
        title: 'Μπορείτε να κλείσετε αυτήν την καρτέλα',
      },
      verified: {
        subtitle: 'Θα ανακατευθυνθείτε σύντομα',
        title: 'Επιτυχής σύνδεση',
      },
      verifiedSwitchTab: {
        subtitle: 'Επιστροφή στην αρχική καρτέλα για να συνεχίσετε',
        subtitleNewTab: 'Επιστροφή στη νέα καρτέλα που άνοιξε για να συνεχίσετε',
        titleNewTab: 'Έχετε συνδεθεί σε άλλη καρτέλα',
      },
    },
    emailLinkMfa: {
      formSubtitle: 'Χρησιμοποιήστε τον σύνδεσμο επαλήθευσης που στάλθηκε στο email σας',
      resendButton: 'Δεν λάβατε τον σύνδεσμο; Επανάληψη αποστολής',
      subtitle: 'για να συνεχίσετε στο {{applicationName}}',
      title: 'Ελέγξτε το email σας',
    },
    enterpriseConnections: {
      subtitle: 'Συνδεθείτε με SSO επιχείρησης',
      title: 'Σύνδεση επιχείρησης',
    },
    forgotPassword: {
      formTitle: 'Επαναφορά κωδικού πρόσβασης',
      resendButton: 'Δεν λάβατε κωδικό; Αποστολή ξανά',
      subtitle: 'για να επαναφέρετε τον κωδικό σας',
      subtitle_email: 'Πρώτα, εισάγετε τον κωδικό που στάλθηκε στη διεύθυνση email σας',
      subtitle_phone: 'Πρώτα, εισάγετε τον κωδικό που στάλθηκε στο τηλέφωνό σας',
      title: 'Επαναφορά κωδικού πρόσβασης',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Επαναφορά κωδικού πρόσβασης',
      label__alternativeMethods: 'Ή, συνδεθείτε με μια άλλη μέθοδο.',
      title: 'Ξεχάσατε τον κωδικό πρόσβασης;',
    },
    newDeviceVerificationNotice:
      'Συνδέεστε από μια νέα συσκευή. Ζητάμε επαλήθευση για να διατηρήσουμε τον λογαριασμό σας ασφαλή.',
    noAvailableMethods: {
      message: 'Δεν είναι δυνατή η σύνδεση. Δεν υπάρχει διαθέσιμος παράγοντας αυθεντικοποίησης.',
      subtitle: 'Προέκυψε σφάλμα',
      title: 'Δεν είναι δυνατή η σύνδεση',
    },
    passkey: {
      subtitle:
        'Η χρήση του passkey σας επιβεβαιώνει την ταυτότητά σας. Η συσκευή σας μπορεί να ζητήσει δακτυλικό αποτύπωμα, αναγνώριση προσώπου ή κλείδωμα οθόνης.',
      title: 'Χρησιμοποιήστε το passkey σας',
    },
    password: {
      actionLink: 'Χρήση άλλης μεθόδου',
      subtitle: 'για να συνεχίσετε στο {{applicationName}}',
      title: 'Εισαγωγή κωδικού πρόσβασης',
    },
    passwordCompromised: {
      title: 'Ο κωδικός πρόσβασης έχει παραβιαστεί',
    },
    passwordPwned: {
      title: 'Παραβιασμένος κωδικός',
    },
    passwordUntrusted: {
      title: 'Μη αξιόπιστος κωδικός πρόσβασης',
    },
    phoneCode: {
      formTitle: 'Κωδικός επαλήθευσης',
      resendButton: 'Δεν λάβατε κωδικό; Αποστολή ξανά',
      subtitle: 'για να συνεχίσετε στο {{applicationName}}',
      title: 'Ελέγξτε το τηλέφωνό σας',
    },
    phoneCodeMfa: {
      formTitle: 'Κωδικός επαλήθευσης',
      resendButton: 'Δεν λάβατε κωδικό; Αποστολή ξανά',
      subtitle: 'για να συνεχίσετε στο {{applicationName}}',
      title: 'Ελέγξτε το τηλέφωνό σας',
    },
    resetPassword: {
      formButtonPrimary: 'Επαναφορά κωδικού πρόσβασης',
      requiredMessage:
        'Υπάρχει ήδη λογαριασμός με μη επαληθευμένη διεύθυνση email. Παρακαλούμε επαναφέρετε τον κωδικό σας για λόγους ασφαλείας.',
      successMessage: 'Ο κωδικός πρόσβασής σας έχει αλλάξει με επιτυχία. Σας συνδέουμε, παρακαλώ περιμένετε.',
      title: 'Επαναφορά κωδικού πρόσβασης',
    },
    resetPasswordMfa: {
      detailsLabel: 'Πρέπει να επαληθεύσουμε την ταυτότητά σας πριν επαναφέρουμε τον κωδικό πρόσβασής σας.',
    },
    start: {
      actionLink: 'Εγγραφή',
      actionLink__join_waitlist: 'Εγγραφή στη λίστα αναμονής',
      actionLink__use_email: 'Χρήση email',
      actionLink__use_email_username: 'Χρήση email ή ονόματος χρήστη',
      actionLink__use_passkey: 'Χρήση passkey',
      actionLink__use_phone: 'Χρήση τηλεφώνου',
      actionLink__use_username: 'Χρήση ονόματος χρήστη',
      actionText: 'Δεν έχετε λογαριασμό;',
      actionText__join_waitlist: 'Θέλετε πρώιμη πρόσβαση;',
      alternativePhoneCodeProvider: {
        actionLink: 'Χρησιμοποιήστε άλλη μέθοδο',
        label: 'Δεν λάβατε κωδικό;',
        subtitle: 'Εάν αντιμετωπίζετε προβλήματα, μπορείτε να λάβετε κωδικό με άλλο τρόπο.',
        title: 'Λάβετε κωδικό με άλλο τρόπο',
      },
      subtitle: 'για να συνεχίσετε στο {{applicationName}}',
      subtitleCombined: 'Συνεχίστε στο {{applicationName}}',
      title: 'Σύνδεση',
      titleCombined: 'Συνέχεια στο {{applicationName}}',
    },
    totpMfa: {
      formTitle: 'Κωδικός επαλήθευσης',
      subtitle: 'για να συνεχίσετε στο {{applicationName}}',
      title: 'Aυθεντικοποίηση δύο βημάτων',
    },
    web3Solana: {
      subtitle: 'Επιλέξτε ένα πορτοφόλι παρακάτω για να συνδεθείτε',
      title: 'Σύνδεση με Solana',
    },
  },
  signInEnterPasswordTitle: 'Εισαγωγή κωδικού πρόσβασης',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: 'Δεν λάβατε κωδικό; Επαναποστολή',
      subtitle: 'Λάβετε έναν κωδικό μέσω {{provider}}',
      title: 'Λάβετε κωδικό με άλλο τρόπο',
    },
    continue: {
      actionLink: 'Σύνδεση',
      actionText: 'Έχετε ήδη λογαριασμό;',
      subtitle: 'για να συνεχίσετε στο {{applicationName}}',
      title: 'Συμπληρώστε τα απαραίτητα πεδία',
    },
    emailCode: {
      formSubtitle: 'Εισαγάγετε τον κωδικό επαλήθευσης που απεστάλη στο email σας',
      formTitle: 'Κωδικός επαλήθευσης',
      resendButton: 'Δεν λάβατε κωδικό; Αποστολή ξανά',
      subtitle: 'για να συνεχίσετε στο {{applicationName}}',
      title: 'Επαληθεύστε το email σας',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'Για να συνεχίσετε, ανοίξτε το σύνδεσμο επαλήθευσης στην ίδια συσκευή και φυλλομετρητή που τον ζητήσατε.',
        title: 'Δεν βρέθηκε έγκυρος σύνδεσμος επαλήθευσης',
      },
      formSubtitle: 'Χρησιμοποιήστε τον σύνδεσμο επαλήθευσης που απεστάλη στη διεύθυνση email σας',
      formTitle: 'Σύνδεσμος επαλήθευσης',
      loading: {
        title: 'Εγγραφή σε εξέλιξη...',
      },
      resendButton: 'Δεν λάβατε σύνδεσμο; Αποστολή ξανά',
      subtitle: 'για να συνεχίσετε στο {{applicationName}}',
      title: 'Επαληθεύστε το email σας',
      verified: {
        title: 'Επιτυχής εγγραφή',
      },
      verifiedSwitchTab: {
        subtitle: 'Επιστροφή στη νέα καρτέλα για να συνεχίσετε',
        subtitleNewTab: 'Επιστροφή στην προηγούμενη καρτέλα για να συνεχίσετε',
        title: 'Επιτυχής επαλήθευση email',
      },
    },
    enterpriseConnections: {
      subtitle: 'Εγγραφείτε με SSO επιχείρησης',
      title: 'Εγγραφή επιχείρησης',
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'Συμφωνώ με την {{ privacyPolicyLink || link("Πολιτική Απορρήτου") }}',
        label__onlyTermsOfService: 'Συμφωνώ με τους {{ termsOfServiceLink || link("Όρους Χρήσης") }}',
        label__termsOfServiceAndPrivacyPolicy:
          'Συμφωνώ με τους {{ termsOfServiceLink || link("Όρους Χρήσης") }} και την {{ privacyPolicyLink || link("Πολιτική Απορρήτου") }}',
      },
      continue: {
        subtitle: 'Παρακαλώ διαβάστε και αποδεχτείτε τους όρους για να συνεχίσετε',
        title: 'Νομική συναίνεση',
      },
    },
    phoneCode: {
      formSubtitle: 'Εισαγάγετε τον κωδικό επαλήθευσης που απεστάλη στον αριθμό τηλεφώνου σας',
      formTitle: 'Κωδικός επαλήθευσης',
      resendButton: 'Δεν λάβατε κωδικό; Αποστολή ξανά',
      subtitle: 'για να συνεχίσετε στο {{applicationName}}',
      title: 'Επαληθεύστε το τηλέφωνό σας',
    },
    restrictedAccess: {
      actionLink: 'Σύνδεση',
      actionText: 'Έχετε ήδη πρόσβαση;',
      blockButton__emailSupport: 'Επικοινωνία με υποστήριξη',
      blockButton__joinWaitlist: 'Εγγραφή στη λίστα αναμονής',
      subtitle: 'Αυτή η εφαρμογή είναι αυτήν τη στιγμή μόνο για προσκεκλημένους.',
      subtitleWaitlist: 'Εγγραφείτε στη λίστα αναμονής για πρώιμη πρόσβαση.',
      title: 'Περιορισμένη πρόσβαση',
    },
    start: {
      actionLink: 'Σύνδεση',
      actionLink__use_email: 'Χρήση email',
      actionLink__use_phone: 'Χρήση τηλεφώνου',
      actionText: 'Έχετε ήδη λογαριασμό;',
      alternativePhoneCodeProvider: {
        actionLink: 'Χρησιμοποιήστε άλλη μέθοδο',
        label: 'Δεν λάβατε κωδικό;',
        subtitle: 'Εάν αντιμετωπίζετε προβλήματα, μπορείτε να λάβετε κωδικό με άλλο τρόπο.',
        title: 'Λάβετε κωδικό με άλλο τρόπο',
      },
      subtitle: 'για να συνεχίσετε στο {{applicationName}}',
      subtitleCombined: 'για να συνεχίσετε στο {{applicationName}}',
      title: 'Δημιουργήστε τον λογαριασμό σας',
      titleCombined: 'Δημιουργήστε τον λογαριασμό σας',
    },
    web3Solana: {
      subtitle: 'Επιλέξτε ένα πορτοφόλι παρακάτω για να εγγραφείτε',
      title: 'Εγγραφή με Solana',
    },
  },
  socialButtonsBlockButton: 'Συνέχεια με {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        'Υπάρχει ήδη οργανισμός για το ανιχνευμένο όνομα εταιρείας ({{organizationName}}) και {{organizationDomain}}. Εγγραφείτε μέσω πρόσκλησης.',
    },
    chooseOrganization: {
      action__createOrganization: 'Δημιουργία νέου οργανισμού',
      action__invitationAccept: 'Συμμετοχή',
      action__suggestionsAccept: 'Αίτημα συμμετοχής',
      subtitle: 'Συμμετάσχετε σε έναν υπάρχοντα οργανισμό ή δημιουργήστε νέο',
      subtitle__createOrganizationDisabled: 'Συμμετάσχετε σε έναν υπάρχοντα οργανισμό',
      suggestionsAcceptedLabel: 'Σε αναμονή έγκρισης',
      title: 'Επιλέξτε οργανισμό',
    },
    createOrganization: {
      formButtonReset: 'Ακύρωση',
      formButtonSubmit: 'Συνέχεια',
      formFieldInputPlaceholder__name: 'Ο οργανισμός μου',
      formFieldInputPlaceholder__slug: 'o-organismos-mou',
      formFieldLabel__name: 'Όνομα',
      formFieldLabel__slug: 'Slug',
      subtitle: 'Εισάγετε τα στοιχεία του οργανισμού σας για να συνεχίσετε',
      title: 'Ρυθμίστε τον οργανισμό σας',
    },
    organizationCreationDisabled: {
      subtitle: 'Επικοινωνήστε με τον διαχειριστή του οργανισμού σας για πρόσκληση.',
      title: 'Πρέπει να ανήκετε σε έναν οργανισμό',
    },
    signOut: {
      actionLink: 'Αποσύνδεση',
      actionText: 'Συνδεδεμένος ως {{identifier}}',
    },
  },
  taskResetPassword: {
    formButtonPrimary: 'Επαναφορά κωδικού πρόσβασης',
    signOut: {
      actionLink: 'Αποσύνδεση',
      actionText: 'Συνδεδεμένος ως {{identifier}}',
    },
    subtitle: 'Για λόγους ασφαλείας, παρακαλώ επαναφέρετε τον κωδικό πρόσβασής σας',
    title: 'Επαναφορά κωδικού πρόσβασης',
  },
  taskSetupMfa: {
    badge: undefined,
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: undefined,
        infoText: undefined,
      },
      addPhoneNumber: undefined,
      cancel: undefined,
      subtitle: undefined,
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyPhone: {
        formButtonPrimary: undefined,
        formTitle: undefined,
        resendButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
    start: {
      methodSelection: {
        phoneCode: undefined,
        totp: undefined,
      },
      subtitle: undefined,
      title: undefined,
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: undefined,
        buttonUnableToScan__nonPrimary: undefined,
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        infoText__ableToScan: undefined,
        infoText__unableToScan: undefined,
        inputLabel__unableToScan1: undefined,
      },
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyTotp: {
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        formTitle: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
  },
  unstable__errors: {
    already_a_member_in_organization: 'Είστε ήδη μέλος σε αυτόν τον οργανισμό',
    avatar_file_size_exceeded:
      'Το μέγεθος του αρχείου υπερβαίνει το μέγιστο όριο των 10 MB. Επιλέξτε ένα μικρότερο αρχείο.',
    avatar_file_type_invalid: 'Ο τύπος αρχείου δεν υποστηρίζεται. Ανεβάστε μια εικόνα JPG, PNG, GIF ή WEBP.',
    captcha_invalid:
      'Η εγγραφή απέτυχε λόγω αποτυχημένων ελέγχων ασφαλείας. Ανανεώστε τη σελίδα για να δοκιμάσετε ξανά ή επικοινωνήστε με το κέντρο υποστήριξης για περισσότερη βοήθεια.',
    captcha_unavailable:
      'Η εγγραφή απέτυχε λόγω αποτυχημένης επαλήθευσης bot. Παρακαλώ ανανεώστε τη σελίδα για να δοκιμάσετε ξανά ή επικοινωνήστε με την υποστήριξη για περισσότερη βοήθεια.',
    form_code_incorrect: 'Ο κωδικός δεν είναι σωστός.',
    form_email_address_blocked:
      'Οι προσωρινές υπηρεσίες email δεν υποστηρίζονται. Παρακαλώ χρησιμοποιήστε τη συνηθισμένη διεύθυνση email σας για να δημιουργήσετε λογαριασμό.',
    form_identifier_exists__email_address: 'Αυτή η διεύθυνση email χρησιμοποιείται ήδη. Παρακαλώ δοκιμάστε άλλη.',
    form_identifier_exists__phone_number: 'Αυτός ο αριθμός τηλεφώνου χρησιμοποιείται ήδη. Παρακαλώ δοκιμάστε άλλον.',
    form_identifier_exists__username: 'Αυτό το όνομα χρήστη χρησιμοποιείται ήδη. Παρακαλώ δοκιμάστε άλλο.',
    form_identifier_not_found: 'Δεν βρέθηκε λογαριασμός με αυτές τις λεπτομέρειες.',
    form_new_password_matches_current:
      'Ο νέος κωδικός πρόσβασης δεν μπορεί να είναι ίδιος με τον τρέχοντα κωδικό πρόσβασης.',
    form_param_format_invalid: '{{paramName}} έχει μη έγκυρη μορφή.',
    form_param_format_invalid__email_address: 'Η διεύθυνση email πρέπει να είναι μια έγκυρη διεύθυνση email.',
    form_param_format_invalid__phone_number: 'Ο αριθμός τηλεφώνου πρέπει να είναι σε έγκυρη διεθνή μορφή',
    form_param_max_length_exceeded__first_name: 'Το όνομα δεν πρέπει να υπερβαίνει τους 256 χαρακτήρες.',
    form_param_max_length_exceeded__last_name: 'Το επώνυμο δεν πρέπει να υπερβαίνει τους 256 χαρακτήρες.',
    form_param_max_length_exceeded__name: 'Το όνομα δεν πρέπει να υπερβαίνει τους 256 χαρακτήρες.',
    form_param_nil: 'Απαιτείται {{paramName}}.',
    form_param_type_invalid: '{{paramName}} έχει μη έγκυρο τύπο.',
    form_param_type_invalid__email_address: 'Η διεύθυνση email δεν είναι έγκυρη.',
    form_param_type_invalid__phone_number: 'Ο αριθμός τηλεφώνου δεν είναι έγκυρος.',
    form_param_value_invalid: '{{paramName}} δεν είναι έγκυρο.',
    form_password_compromised__sign_in:
      'Ο κωδικός πρόσβασής σας ενδέχεται να έχει παραβιαστεί. Για την προστασία του λογαριασμού σας, παρακαλώ συνεχίστε με εναλλακτική μέθοδο σύνδεσης. Θα σας ζητηθεί να επαναφέρετε τον κωδικό πρόσβασής σας μετά τη σύνδεση.',
    form_password_incorrect: 'Ο κωδικός πρόσβασης δεν είναι σωστός.',
    form_password_length_too_short:
      'Ο κωδικός πρόσβασής σας πρέπει να αποτελείται από τουλάχιστον {{length}} χαρακτήρες.',
    form_password_not_strong_enough: 'Ο κωδικός πρόσβασής σας δεν είναι αρκετά ισχυρός.',
    form_password_or_identifier_incorrect:
      'Ο κωδικός πρόσβασης ή η διεύθυνση email είναι λανθασμένη. Δοκιμάστε ξανά ή χρησιμοποιήστε άλλη μέθοδο.',
    form_password_pwned:
      'Αυτός ο κωδικός πρόσβασης έχει διαρρεύσει online στο παρελθόν και δεν μπορεί να χρησιμοποιηθεί. Δοκιμάστε έναν άλλο κωδικό πρόσβασης αντί για αυτόν.',
    form_password_pwned__sign_in:
      'Αυτός ο κωδικός πρόσβασης εντοπίστηκε σε παραβίαση δεδομένων και πρέπει να επαναφερθεί. Παρακαλώ δοκιμάστε έναν άλλο κωδικό πρόσβασης ή χρησιμοποιήστε τον σύνδεσμο "Ξεχάσατε τον κωδικό πρόσβασης;" για να τον επαναφέρετε.',
    form_password_size_in_bytes_exceeded:
      'Ο κωδικός πρόσβασής σας έχει υπερβεί το μέγιστο αριθμό bytes που επιτρέπεται. Παρακαλούμε, συντομεύστε τον ή αφαιρέστε μερικούς ειδικούς χαρακτήρες.',
    form_password_untrusted__sign_in:
      'Για λόγους ασφαλείας, απαιτείται επαναφορά κωδικού πρόσβασης. Παρακαλώ χρησιμοποιήστε τον σύνδεσμο "Ξεχάσατε τον κωδικό πρόσβασης;" για να τον επαναφέρετε.',
    form_password_validation_failed: 'Λανθασμένος κωδικός',
    form_username_invalid_character:
      'Το όνομα χρήστη μπορεί να περιέχει μόνο αλφαριθμητικούς χαρακτήρες και κάτω παύλες.',
    form_username_invalid_length: 'Το όνομα χρήστη πρέπει να αποτελείται από 4 έως 64 χαρακτήρες.',
    form_username_needs_non_number_char: 'Το όνομα χρήστη πρέπει να περιέχει τουλάχιστον έναν μη αριθμητικό χαρακτήρα.',
    identification_deletion_failed: 'Δεν μπορείτε να διαγράψετε το τελευταίο στοιχείο ταυτοποιησής σας.',
    not_allowed_access:
      "Η διεύθυνση email ή το τηλέφωνο δεν επιτρέπεται για την εγγραφή. Αυτό μπορεί να οφείλεται στη χρήση '+', '=', '#' ή '.' στην διεύθυνση email σας, χρήση πεδίου που συνδέεται με υπηρεσία email, ή εμφανής αποκλεισμός. Αν πιστεύετε ότι αυτό είναι ένα σφάλμα, παρακαλούμε επικοινωνήστε με την υποστήριξη.",
    organization_domain_blocked: 'Ο τομέας {{domain}} δεν μπορεί να επαληθευτεί.',
    organization_domain_common: 'Ο τομέας {{domain}} δεν μπορεί να επαληθευτεί επειδή είναι κοινός τομέας email.',
    organization_domain_exists_for_enterprise_connection:
      'Ο τομέας {{domain}} έχει ήδη επαληθευτεί για σύνδεση επιχείρησης.',
    organization_membership_quota_exceeded: 'Έχετε φτάσει το μέγιστο αριθμό μελών για αυτόν τον οργανισμό.',
    organization_minimum_permissions_needed: 'Δεν έχετε τα απαραίτητα δικαιώματα για να εκτελέσετε αυτή την ενέργεια.',
    organization_not_found_or_unauthorized: 'Ο οργανισμός δεν βρέθηκε ή δεν έχετε δικαίωμα πρόσβασης.',
    organization_not_found_or_unauthorized_with_create_organization_disabled:
      'Δεν έχετε πρόσβαση σε κανέναν οργανισμό. Επικοινωνήστε με τον διαχειριστή του οργανισμού σας για πρόσκληση.',
    passkey_already_exists: 'Ένα passkey έχει ήδη καταχωρηθεί σε αυτή τη συσκευή.',
    passkey_not_supported: 'Τα passkeys δεν υποστηρίζονται σε αυτή τη συσκευή.',
    passkey_pa_not_supported: 'Η εγγραφή απαιτεί έναν επαληθευτή πλατφόρμας, αλλά η συσκευή δεν υποστηρίζει έναν.',
    passkey_registration_cancelled: 'Η εγγραφή passkey ακυρώθηκε ή έληξε.',
    passkey_retrieval_cancelled: 'Η επαλήθευση passkey ακυρώθηκε ή έληξε.',
    passwordComplexity: {
      maximumLength: 'λιγότερους από {{length}} χαρακτήρες',
      minimumLength: '{{length}} ή περισσότερους χαρακτήρες',
      requireLowercase: 'ένα πεζό γράμμα',
      requireNumbers: 'έναν αριθμό',
      requireSpecialCharacter: 'ένα ειδικό χαρακτήρα',
      requireUppercase: 'ένα κεφαλαίο γράμμα',
      sentencePrefix: 'Ο κωδικός πρόσβασής σας πρέπει να περιέχει',
    },
    phone_number_exists: 'Αυτός ο αριθμός τηλεφώνου χρησιμοποιείται ήδη. Δοκιμάστε έναν άλλο.',
    session_exists: 'Έχετε ήδη συνδεθεί.',
    web3_missing_identifier: 'Δεν εντοπίστηκε αναγνωριστικό Web3. Παρακαλώ δοκιμάστε ξανά.',
    web3_signature_request_rejected: 'Απορρίψατε το αίτημα υπογραφής. Δοκιμάστε ξανά για να συνεχίσετε.',
    web3_solana_signature_generation_failed:
      'Παρουσιάστηκε σφάλμα κατά τη δημιουργία της υπογραφής. Δοκιμάστε ξανά για να συνεχίσετε.',
    zxcvbn: {
      couldBeStronger:
        'Ο κωδικός πρόσβασής σας είναι αρκετός, αλλά θα μπορούσε να είναι πιο ισχυρός. Δοκιμάστε να προσθέσετε περισσότερους χαρακτήρες.',
      goodPassword: 'Ο κωδικός πρόσβασής σας πληροί όλες τις απαιτούμενες προδιαγραφές.',
      notEnough: 'Ο κωδικός πρόσβασής σας δεν είναι αρκετά ισχυρός.',
      suggestions: {
        allUppercase: 'Έχετε μόνο μερικά κεφαλαία γράμματα.',
        anotherWord: 'Προσθέστε περισσότερες λέξεις που είναι λιγότερο συνηθισμένες.',
        associatedYears: 'Αποφύγετε έτη που σας αφορούν.',
        capitalization: 'Μην έχετε κεφαλαίο μόνο το πρώτο γράμμα.',
        dates: 'Αποφύγετε ημερομηνίες και έτη που σας αφορούν.',
        l33t: "Αποφύγετε προβλέψιμες αντικαταστάσεις γραμμάτων όπως '@' για 'a'.",
        longerKeyboardPattern:
          'Χρησιμοποιήστε μεγαλύτερα μοτίβα πληκτρολογίου και αλλάξτε πολλές φορές την κατεύθυνση πληκτρολόγησης.',
        noNeed:
          'Μπορείτε να δημιουργήσετε ισχυρούς κωδικούς πρόσβασης χωρίς τη χρήση συμβόλων, αριθμών ή κεφαλαίων γραμμάτων.',
        pwned: 'Αν χρησιμοποιείτε αυτόν τον κωδικό πρόσβασης και αλλού, θα πρέπει να τον αλλάξετε.',
        recentYears: 'Αποφύγετε πρόσφατα έτη.',
        repeated: 'Αποφύγετε επαναλαμβανόμενες λέξεις και χαρακτήρες.',
        reverseWords: 'Αποφύγετε αντιστροφές συνηθισμένων λέξεων.',
        sequences: 'Αποφύγετε κοινές ακολουθίες χαρακτήρων.',
        useWords: 'Χρησιμοποιήστε πολλές λέξεις, αλλά αποφύγετε κοινές φράσεις.',
      },
      warnings: {
        common: 'Αυτός είναι ένας κοινός κωδικός πρόσβασης.',
        commonNames: 'Συνηθισμένα ονόματα και επώνυμα είναι εύκολα να μαντευτούν.',
        dates: 'Ημερομηνίες είναι εύκολες να μαντευτούν.',
        extendedRepeat: 'Επαναλαμβανόμενα μοτίβα χαρακτήρων όπως "abcabcabc" είναι εύκολα να μαντευτούν.',
        keyPattern: 'Σύντομα μοτίβα πληκτρολογίου είναι εύκολα να μαντευτούν.',
        namesByThemselves: 'Μεμονωμένα ονόματα ή επώνυμα είναι εύκολα να μαντευτούν.',
        pwned: 'Ο κωδικός πρόσβασής σας αποκαλύφθηκε από παραβίαση δεδομένων στο διαδίκτυο.',
        recentYears: 'Πρόσφατα έτη είναι εύκολα να μαντευτούν.',
        sequences: 'Συνηθισμένες ακολουθίες χαρακτήρων όπως "abc" είναι εύκολα να μαντευτούν.',
        similarToCommon: 'Αυτός είναι παρόμοιος με έναν κοινό κωδικό πρόσβασης.',
        simpleRepeat: 'Επαναλαμβανόμενοι χαρακτήρες όπως "aaa" είναι εύκολο να μαντευτούν.',
        straightRow: 'Σειρές γραμμάτων στο πληκτρολόγιο είναι εύκολα να μαντευτούν.',
        topHundred: 'Αυτός είναι ένας συχνά χρησιμοποιούμενος κωδικός πρόσβασης.',
        topTen: 'Αυτός είναι ένας πολύ διαδεδομένος κωδικός πρόσβασης.',
        userInputs: 'Δεν πρέπει να υπάρχουν προσωπικά ή σχετικά με τη σελίδα δεδομένα.',
        wordByItself: 'Οι μεμονωμένες λέξεις είναι εύκολες να μαντευτούν.',
      },
    },
  },
  userButton: {
    action__addAccount: 'Προσθήκη λογαριασμού',
    action__closeUserMenu: 'Κλείσιμο μενού χρήστη',
    action__manageAccount: 'Διαχείριση λογαριασμού',
    action__openUserMenu: 'Άνοιγμα μενού χρήστη',
    action__signOut: 'Αποσύνδεση',
    action__signOutAll: 'Αποσύνδεση από όλους τους λογαριασμούς',
  },
  userProfile: {
    apiKeysPage: {
      title: 'Κλειδιά API',
    },
    backupCodePage: {
      actionLabel__copied: 'Αντιγράφηκαν!',
      actionLabel__copy: 'Αντιγραφή όλων',
      actionLabel__download: 'Λήψη .txt',
      actionLabel__print: 'Εκτύπωση',
      infoText1: 'Οι εφεδρικοί κωδικοί θα είναι ενεργοποιημένοι για αυτόν τον λογαριασμό.',
      infoText2:
        'Φυλάξτε τους εφεδρικούς κωδικούς μυστικούς και αποθηκεύστε τους με ασφάλεια. Μπορείτε να δημιουργήσετε νέους εφεδρικούς κωδικούς εάν υποψιάζεστε ότι έχουν διαρρεύσει.',
      subtitle__codelist: 'Φυλάξτε τους με ασφάλεια και κρατήστε τους μυστικούς.',
      successMessage:
        'Οι εφεδρικοί κωδικοί είναι πλέον ενεργοποιημένοι. Μπορείτε να χρησιμοποιήσετε έναν από αυτούς για να συνδεθείτε στον λογαριασμό σας, εάν χάσετε την πρόσβαση στη συσκευή επαλήθευσής σας. Κάθε κωδικός μπορεί να χρησιμοποιηθεί μόνο μία φορά.',
      successSubtitle:
        'Μπορείτε να χρησιμοποιήσετε έναν από αυτούς για να συνδεθείτε στον λογαριασμό σας, εάν χάσετε την πρόσβαση στη συσκευή επαλήθευσής σας.',
      title: 'Προσθήκη επαλήθευσης με εφεδρικούς κωδικούς',
      title__codelist: 'Εφεδρικοί κωδικοί',
    },
    billingPage: {
      paymentHistorySection: {
        empty: 'Δεν υπάρχει ιστορικό πληρωμών',
        notFound: 'Δεν βρέθηκαν πληρωμές',
        tableHeader__amount: 'Ποσό',
        tableHeader__date: 'Ημερομηνία',
        tableHeader__status: 'Κατάσταση',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Ορισμός ως προεπιλογή',
        actionLabel__remove: 'Αφαίρεση',
        add: 'Προσθήκη μεθόδου πληρωμής',
        addSubtitle: 'Προσθέστε μια νέα μέθοδο πληρωμής στον λογαριασμό σας',
        cancelButton: 'Ακύρωση',
        formButtonPrimary__add: 'Προσθήκη',
        formButtonPrimary__pay: 'Πληρωμή',
        payWithTestCardButton: 'Πληρωμή με δοκιμαστική κάρτα',
        removeMethod: {
          messageLine1: 'Η μέθοδος πληρωμής {{identifier}} θα αφαιρεθεί από τον λογαριασμό σας',
          messageLine2: 'Δεν θα μπορείτε πλέον να χρησιμοποιήσετε αυτή τη μέθοδο πληρωμής',
          successMessage: 'Η μέθοδος πληρωμής αφαιρέθηκε επιτυχώς',
          title: 'Αφαίρεση μεθόδου πληρωμής',
        },
        title: 'Μέθοδοι πληρωμής',
      },
      start: {
        headerTitle__payments: 'Πληρωμές',
        headerTitle__plans: 'Πλάνα',
        headerTitle__statements: 'Καταστάσεις',
        headerTitle__subscriptions: 'Συνδρομές',
      },
      statementsSection: {
        empty: 'Δεν υπάρχουν καταστάσεις',
        itemCaption__paidForPlan: 'Πληρώθηκε για {{planName}}',
        itemCaption__proratedCredit: 'Πίστωση αναλογικής κατανομής',
        itemCaption__subscribedAndPaidForPlan: 'Εγγραφή και πληρωμή για {{planName}}',
        notFound: 'Δεν βρέθηκαν καταστάσεις',
        tableHeader__amount: 'Ποσό',
        tableHeader__date: 'Ημερομηνία',
        title: 'Καταστάσεις',
        totalPaid: 'Συνολικό καταβληθέν ποσό',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Διαχείριση συνδρομής',
        actionLabel__newSubscription: 'Νέα συνδρομή',
        actionLabel__switchPlan: 'Αλλαγή πλάνου',
        tableHeader__edit: 'Επεξεργασία',
        tableHeader__plan: 'Πλάνο',
        tableHeader__startDate: 'Ημερομηνία έναρξης',
        title: 'Συνδρομές',
      },
      subscriptionsSection: {
        actionLabel__default: 'Προεπιλογή',
      },
      switchPlansSection: {
        title: 'Αλλαγή πλάνου',
      },
      title: 'Χρεώσεις',
    },
    connectedAccountPage: {
      formHint: 'Επιλέξτε έναν πάροχο για να συνδέσετε τον λογαριασμό σας.',
      formHint__noAccounts: 'Δεν υπάρχουν διαθέσιμοι πάροχοι εξωτερικών λογαριασμών.',
      removeResource: {
        messageLine1: 'Ο {{identifier}} θα αφαιρεθεί από αυτόν τον λογαριασμό.',
        messageLine2:
          'Δεν θα μπορείτε πλέον να χρησιμοποιήσετε αυτόν τον συνδεδεμένο λογαριασμό και οποιεσδήποτε εξαρτημένες λειτουργίες δεν θα λειτουργούν πλέον.',
        successMessage: 'Ο {{connectedAccount}} έχει αφαιρεθεί από τον λογαριασμό σας.',
        title: 'Αφαίρεση συνδεδεμένου λογαριασμού',
      },
      socialButtonsBlockButton: 'Σύνδεση με τον λογαριασμό {{provider|titleize}}',
      successMessage: 'Ο πάροχος έχει προστεθεί στον λογαριασμό σας',
      title: 'Προσθήκη συνδεδεμένου λογαριασμού',
    },
    deletePage: {
      actionDescription: 'Πληκτρολογήστε "Διαγραφή λογαριασμού" παρακάτω για να συνεχίσετε.',
      confirm: 'Διαγραφή λογαριασμού',
      messageLine1:
        'Είστε βέβαιοι ότι θέλετε να διαγράψετε τον λογαριασμό σας; Ορισμένα σχετικά δεδομένα ενδέχεται να διατηρηθούν. Για να ζητήσετε πλήρη διαγραφή δεδομένων, επικοινωνήστε με την υποστήριξη.',
      messageLine2: 'Αυτή η ενέργεια είναι μόνιμη και μη αναστρέψιμη.',
      title: 'Διαγραφή λογαριασμού',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'Θα σταλεί ένα email που περιέχει έναν κωδικό επαλήθευσης σε αυτήν τη διεύθυνση email.',
        formSubtitle: 'Εισαγάγετε τον κωδικό επαλήθευσης που εστάλη στην {{identifier}}',
        formTitle: 'Κωδικός επαλήθευσης',
        resendButton: 'Δεν λάβατε κωδικό; Επανάληψη αποστολής',
        successMessage: 'Το email {{identifier}} έχει προστεθεί στον λογαριασμό σας.',
      },
      emailLink: {
        formHint: 'Θα σταλεί ένα email που περιέχει έναν σύνδεσμο επαλήθευσης σε αυτήν τη διεύθυνση email.',
        formSubtitle: 'Κάντε κλικ στον σύνδεσμο επαλήθευσης στο email που εστάλη στην {{identifier}}',
        formTitle: 'Σύνδεσμος επαλήθευσης',
        resendButton: 'Δεν λάβατε κάποιον σύνδεσμο; Επανάληψη αποστολής',
        successMessage: 'Το email {{identifier}} έχει προστεθεί στον λογαριασμό σας.',
      },
      enterpriseSSOLink: {
        formButton: 'Επαλήθευση',
        formSubtitle: 'Θα σταλεί ένα σύνδεσμος επαλήθευσης μέσω της σύνδεσης SSO επιχείρησης σας',
      },
      formHint: 'Εισάγετε μια νέα διεύθυνση email για να την προσθέσετε στον λογαριασμό σας',
      removeResource: {
        messageLine1: 'Η διεύθυνση {{identifier}} θα αφαιρεθεί από αυτόν τον λογαριασμό.',
        messageLine2: 'Δεν θα μπορείτε πλέον να συνδεθείτε χρησιμοποιώντας αυτήν τη διεύθυνση email.',
        successMessage: 'Η διεύθυνση {{emailAddress}} έχει αφαιρεθεί από τον λογαριασμό σας.',
        title: 'Αφαίρεση διεύθυνσης email',
      },
      title: 'Προσθήκη διεύθυνσης email',
      verifyTitle: 'Επαλήθευση διεύθυνσης email',
    },
    formButtonPrimary__add: 'Προσθήκη',
    formButtonPrimary__continue: 'Συνέχεια',
    formButtonPrimary__finish: 'Ολοκλήρωση',
    formButtonPrimary__remove: 'Αφαίρεση',
    formButtonPrimary__save: 'Αποθήκευση',
    formButtonReset: 'Ακύρωση',
    mfaPage: {
      formHint: 'Επιλέξτε μια μέθοδο για προσθήκη.',
      title: 'Προσθήκη διπλής επαλήθευσης',
    },
    mfaPhoneCodePage: {
      backButton: 'Χρήση υπάρχοντος αριθμού',
      primaryButton__addPhoneNumber: 'Προσθήκη αριθμού τηλεφώνου',
      removeResource: {
        messageLine1: 'Ο {{identifier}} δεν θα λαμβάνει πλέον κωδικούς επαλήθευσης κατά τη σύνδεση.',
        messageLine2: 'Ο λογαριασμός σας ενδέχεται να μην είναι τόσο ασφαλής. Είστε σίγουροι ότι θέλετε να συνεχίσετε;',
        successMessage: 'Η διπλή επαλήθευση με κωδικούς SMS έχει αφαιρεθεί για τον αριθμό τηλεφώνου {{mfaPhoneCode}}',
        title: 'Αφαίρεση διπλής επαλήθευσης',
      },
      subtitle__availablePhoneNumbers:
        'Επιλέξτε έναν αριθμό τηλεφώνου για να εγγραφείτε για τη διπλή επαλήθευση με κωδικούς SMS.',
      subtitle__unavailablePhoneNumbers:
        'Δεν υπάρχουν διαθέσιμοι αριθμοί τηλεφώνου για εγγραφή στην διπλή επαλήθευση με κωδικούς SMS.',
      successMessage1:
        'Κατά τη σύνδεση, θα χρειαστεί να εισάγετε έναν κωδικό επαλήθευσης που στάλθηκε σε αυτόν τον αριθμό τηλεφώνου ως επιπλέον βήμα.',
      successMessage2:
        'Αποθηκεύστε αυτούς τους εφεδρικούς κωδικούς και φυλάξτε τους με ασφάλεια. Εάν χάσετε την πρόσβαση στη συσκευή επαλήθευσής σας, μπορείτε να χρησιμοποιήσετε εφεδρικούς κωδικούς για σύνδεση.',
      successTitle: 'Επαλήθευση κωδικού SMS ενεργοποιήθηκε',
      title: 'Προσθήκη επαλήθευσης κωδικού SMS',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Σάρωση QR κωδικού αντί αυτού',
        buttonUnableToScan__nonPrimary: 'Δεν μπορείτε να σαρώσετε τον QR κωδικό;',
        infoText__ableToScan:
          'Ρυθμίστε μια νέα μέθοδο σύνδεσης στην εφαρμογή αυθεντικοποίησης σας και σαρώστε τον παρακάτω QR κωδικό για να τον συνδέσετε με τον λογαριασμό σας.',
        infoText__unableToScan:
          'Ρυθμίστε μια νέα μέθοδο σύνδεσης στην αυθεντικοποίηση και εισαγάγετε τον κλειδί που παρέχεται παρακάτω.',
        inputLabel__unableToScan1:
          'Βεβαιωθείτε ότι οι κωδικοί που βασίζονται στον χρόνο ή μίας χρήσης είναι ενεργοποιημένοι και ολοκληρώστε τη σύνδεση του λογαριασμού σας.',
        inputLabel__unableToScan2:
          'Εναλλακτικά, εάν η εφαρμογή αυθεντικοποίησής σας υποστηρίζει TOTP URIs, μπορείτε επίσης να αντιγράψετε τον πλήρη URI.',
      },
      removeResource: {
        messageLine1:
          'Δεν θα απαιτούνται πλέον κωδικοί επαλήθευσης από αυτήν την εφαρμογή αυθεντικοποίησης κατά τη σύνδεση.',
        messageLine2: 'Ο λογαριασμός σας ενδέχεται να μην είναι τόσο ασφαλής. Είστε σίγουροι ότι θέλετε να συνεχίσετε;',
        successMessage: 'Η διπλή επαλήθευση μέσω εφαρμογής αυθεντικοποίησης έχει αφαιρεθεί.',
        title: 'Αφαίρεση διπλής επαλήθευσης',
      },
      successMessage:
        'Η διπλή επαλήθευση είναι πλέον ενεργοποιημένη. Κατά τη σύνδεση, θα πρέπει να εισαγάγετε έναν κωδικό επαλήθευσης από αυτήν την εφαρμογή αυθεντικοποίησης ως επιπλέον βήμα.',
      title: 'Προσθήκη εφαρμογής αυθεντικοποίησης',
      verifySubtitle: 'Εισαγάγετε τον κωδικό επαλήθευσης που δημιουργήθηκε από την εφαρμογή αυθεντικοποίησης',
      verifyTitle: 'Κωδικός επαλήθευσης',
    },
    mobileButton__menu: 'Μενού',
    navbar: {
      account: 'Προφίλ',
      apiKeys: 'Κλειδιά API',
      billing: 'Χρεώσεις',
      description: 'Διαχειριστείτε τις πληροφορίες του λογαριασμού σας.',
      security: 'Ασφάλεια',
      title: 'Λογαριασμός',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: 'Το {{name}} θα αφαιρεθεί από αυτόν τον λογαριασμό.',
        title: 'Αφαίρεση passkey',
      },
      subtitle__rename: 'Μπορείτε να μετονομάσετε το passkey για να το βρείτε ευκολότερα.',
      title__rename: 'Μετονομασία passkey',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'Συνιστάται η αποσύνδεση από όλες τις άλλες συσκευές που μπορεί να έχουν χρησιμοποιήσει τον παλιό σας κωδικό πρόσβασης.',
      readonly:
        'Ο κωδικός πρόσβασής σας δεν μπορεί να επεξεργαστεί αυτήν τη στιγμή επειδή μπορείτε να συνδεθείτε μόνο μέσω της σύνδεσης με την επιχείρηση.',
      successMessage__set: 'Ο κωδικός πρόσβασής σας έχει οριστεί.',
      successMessage__signOutOfOtherSessions: 'Όλες οι άλλες συνεδρίες έχουν αποσυνδεθεί.',
      successMessage__update: 'Ο κωδικός πρόσβασής σας έχει ενημερωθεί.',
      title__set: 'Ορισμός κωδικού πρόσβασης',
      title__update: 'Αλλαγή κωδικού πρόσβασης',
    },
    phoneNumberPage: {
      infoText: 'Θα σταλεί ένα μήνυμα κειμένου που περιέχει ένα σύνδεσμο επαλήθευσης σε αυτόν τον αριθμό τηλεφώνου.',
      removeResource: {
        messageLine1: 'Ο αριθμός {{identifier}} θα αφαιρεθεί από αυτόν τον λογαριασμό.',
        messageLine2: 'Δεν θα μπορείτε πλέον να συνδεθείτε χρησιμοποιώντας αυτόν τον αριθμό τηλεφώνου.',
        successMessage: 'Ο αριθμός τηλεφώνου {{phoneNumber}} έχει αφαιρεθεί από τον λογαριασμό σας.',
        title: 'Αφαίρεση αριθμού τηλεφώνου',
      },
      successMessage: 'Ο αριθμός τηλεφώνου {{identifier}} έχει προστεθεί στον λογαριασμό σας.',
      title: 'Προσθήκη αριθμού τηλεφώνου',
      verifySubtitle: 'Εισάγετε τον κωδικό επαλήθευσης που στάλθηκε στο {{identifier}}',
      verifyTitle: 'Επαλήθευση αριθμού τηλεφώνου',
    },
    plansPage: {
      title: 'Πλάνα',
    },
    profilePage: {
      fileDropAreaHint: 'Ανεβάστε μια εικόνα σε μορφή JPG, PNG, GIF ή WEBP μικρότερη των 10 MB',
      imageFormDestructiveActionSubtitle: 'Αφαίρεση εικόνας',
      imageFormSubtitle: 'Ανέβασμα εικόνας',
      imageFormTitle: 'Εικόνα προφίλ',
      readonly:
        'Οι πληροφορίες του προφίλ σας έχουν παρασχεθεί από τη σύνδεση με την επιχείρηση και δεν μπορούν να επεξεργαστούν.',
      successMessage: 'Το προφίλ σας έχει ενημερωθεί.',
      title: 'Ενημέρωση προφίλ',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Αποσύνδεση από τη συσκευή',
        title: 'Ενεργές συσκευές',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Προσπάθεια ξανά',
        actionLabel__reauthorize: 'Εξουσιοδοτήστε τώρα',
        destructiveActionTitle: 'Αφαίρεση',
        primaryButton: 'Σύνδεση λογαριασμού',
        subtitle__disconnected: 'Αυτός ο λογαριασμός έχει αποσυνδεθεί.',
        subtitle__reauthorize:
          'Τα απαιτούμενα δικαιώματα έχουν ενημερωθεί και ενδέχεται να αντιμετωπίζετε περιορισμένη λειτουργικότητα. Παρακαλώ επανεξουσιοδοτήστε αυτήν την εφαρμογή για να αποφύγετε προβλήματα',
        title: 'Συνδεδεμένοι λογαριασμοί',
      },
      dangerSection: {
        deleteAccountButton: 'Διαγραφή λογαριασμού',
        title: 'Κίνδυνος',
      },
      emailAddressesSection: {
        destructiveAction: 'Αφαίρεση διεύθυνσης email',
        detailsAction__nonPrimary: 'Ορισμός ως κύρια',
        detailsAction__primary: 'Ολοκλήρωση επαλήθευσης',
        detailsAction__unverified: 'Ολοκλήρωση επαλήθευσης',
        primaryButton: 'Προσθήκη διεύθυνσης email',
        title: 'Διευθύνσεις email',
      },
      enterpriseAccountsSection: {
        primaryButton: 'Σύνδεση λογαριασμού',
        title: 'Επιχειρησιακοί λογαριασμοί',
      },
      headerTitle__account: 'Λογαριασμός',
      headerTitle__security: 'Ασφάλεια',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Επαναδημιουργία κωδικών',
          headerTitle: 'Εφεδρικοί κωδικοί',
          subtitle__regenerate:
            'Λάβετε ένα νέο σετ ασφαλών εφεδρικών κωδικών. Οι προηγούμενοι εφεδρικοί κωδικοί θα διαγραφούν και δεν μπορούν να χρησιμοποιηθούν.',
          title__regenerate: 'Επαναδημιουργία εφεδρικών κωδικών',
        },
        phoneCode: {
          actionLabel__setDefault: 'Ορισμός ως προεπιλεγμένος',
          destructiveActionLabel: 'Αφαίρεση αριθμού τηλεφώνου',
        },
        primaryButton: 'Προσθήκη αυθεντικοποίησης δύο βημάτων',
        title: 'Αυθεντικοποίηση δύο βημάτων',
        totp: {
          destructiveActionTitle: 'Αφαίρεση',
          headerTitle: 'Εφαρμογή αυθεντικοποίησης',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'Αφαίρεση',
        menuAction__rename: 'Μετονομασία',
        primaryButton: 'Προσθήκη passkey',
        title: 'Passkeys',
      },
      passwordSection: {
        primaryButton__setPassword: 'Ορισμός κωδικού πρόσβασης',
        primaryButton__updatePassword: 'Αλλαγή κωδικού πρόσβασης',
        title: 'Κωδικός πρόσβασης',
      },
      phoneNumbersSection: {
        destructiveAction: 'Αφαίρεση αριθμού τηλεφώνου',
        detailsAction__nonPrimary: 'Ορισμός ως κύριος',
        detailsAction__primary: 'Ολοκλήρωση επαλήθευσης',
        detailsAction__unverified: 'Ολοκλήρωση επαλήθευσης',
        primaryButton: 'Προσθήκη αριθμού τηλεφώνου',
        title: 'Αριθμοί τηλεφώνου',
      },
      profileSection: {
        primaryButton: 'Ενημέρωση προφίλ',
        title: 'Προφίλ',
      },
      usernameSection: {
        primaryButton__setUsername: 'Ορισμός ονόματος χρήστη',
        primaryButton__updateUsername: 'Αλλαγή ονόματος χρήστη',
        title: 'Όνομα χρήστη',
      },
      web3WalletsSection: {
        destructiveAction: 'Αφαίρεση πορτοφολιού',
        detailsAction__nonPrimary: 'Ορισμός ως κύριο',
        primaryButton: 'Πορτοφόλια Web3',
        title: 'Πορτοφόλια Web3',
        web3SelectSolanaWalletScreen: {
          subtitle: 'Επιλέξτε ένα πορτοφόλι Solana για σύνδεση με τον λογαριασμό σας.',
          title: 'Προσθήκη πορτοφολιού Solana',
        },
      },
    },
    usernamePage: {
      successMessage: 'Το όνομα χρήστη σας έχει ενημερωθεί.',
      title__set: 'Ενημέρωση ονόματος χρήστη',
      title__update: 'Ενημέρωση ονόματος χρήστη',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: 'Το {{identifier}} θα αφαιρεθεί από αυτόν τον λογαριασμό.',
        messageLine2: 'Δεν θα μπορείτε πλέον να συνδεθείτε χρησιμοποιώντας αυτό το web3 πορτοφόλι.',
        successMessage: 'Το {{web3Wallet}} έχει αφαιρεθεί από τον λογαριασμό σας.',
        title: 'Αφαίρεση web3 πορτοφολιού',
      },
      subtitle__availableWallets: 'Επιλέξτε ένα web3 πορτοφόλι για να το συνδέσετε στον λογαριασμό σας.',
      subtitle__unavailableWallets: 'Δεν υπάρχουν διαθέσιμα web3 πορτοφόλια.',
      successMessage: 'Το πορτοφόλι έχει προστεθεί στον λογαριασμό σας.',
      title: 'Προσθήκη web3 πορτοφολιού',
      web3WalletButtonsBlockButton: 'Συνέχεια με {{provider|titleize}}',
    },
  },
  waitlist: {
    start: {
      actionLink: 'Σύνδεση',
      actionText: 'Έχετε ήδη πρόσβαση;',
      formButton: 'Εγγραφή στη λίστα αναμονής',
      subtitle:
        'Εισάγετε τη διεύθυνση ηλεκτρονικού ταχυδρομείου σας και θα σας ενημερώσουμε όταν η θέση σας είναι έτοιμη',
      title: 'Εγγραφή στη λίστα αναμονής',
    },
    success: {
      message: 'Θα ανακατευθυνθείτε σύντομα...',
      subtitle: 'Θα επικοινωνήσουμε μαζί σας όταν η θέση σας είναι έτοιμη',
      title: 'Ευχαριστούμε που εγγραφήκατε στη λίστα αναμονής!',
    },
  },
  web3SolanaWalletButtons: {
    connect: 'Σύνδεση με {{walletName}}',
    continue: 'Συνέχεια με {{walletName}}',
    noneAvailable:
      'Δεν εντοπίστηκαν πορτοφόλια Solana Web3. Εγκαταστήστε ένα {{ solanaWalletsLink || link("wallet extension") }} με υποστήριξη Web3.',
  },
} as const;
